import type { ReactElement } from 'react';
import { useSystemHealth, type ServiceHealth } from '@/lib/queries/admin';
import { AdminCardError } from '@/components/atoms/admin-card-error';

export function AdminSystemHealthRow(): ReactElement {
  const { data, isError } = useSystemHealth();

  if (isError) return <AdminCardError label="system health" />;
  if (!data) return <SkeletonRow />;

  return (
    <div className="grid grid-cols-3 gap-3">
      <StorageCard />
      <LastBackupCard />
      <ServiceStatusCard services={data.services} />
    </div>
  );
}

function StorageCard(): ReactElement {
  return (
    <HealthCard heading="Storage">
      <Placeholder text="Storage data coming soon" />
    </HealthCard>
  );
}

function LastBackupCard(): ReactElement {
  return (
    <HealthCard heading="Last backup">
      <Placeholder text="Backup data coming soon" />
    </HealthCard>
  );
}

const SERVICE_STATE_CLASS: Readonly<Record<string, string>> = {
  running: 'bg-[var(--ok)]',
  degraded: 'bg-[var(--warn)]',
  down: 'bg-[var(--err)]',
};

function ServiceStatusCard({ services }: { services: ServiceHealth[] }): ReactElement {
  return (
    <HealthCard heading="Service status">
      {services.length === 0 ? (
        <Placeholder text="No services reported" />
      ) : (
        <ul className="flex flex-col gap-2">
          {services.map((s) => (
            <li key={s.name} className="flex items-center gap-2 text-[12px]">
              <span
                className={`block h-2 w-2 rounded-full ${SERVICE_STATE_CLASS[s.state] ?? 'bg-[var(--ink-4)]'}`}
                aria-label={`${s.name} ${s.state}`}
                role="img"
              />
              <span className="truncate">{s.name}</span>
            </li>
          ))}
        </ul>
      )}
    </HealthCard>
  );
}

function HealthCard({ heading, children }: { heading: string; children: React.ReactNode }): ReactElement {
  return (
    <section className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4">
      <h3 className="text-[12px] font-semibold mb-3">{heading}</h3>
      {children}
    </section>
  );
}

function Placeholder({ text }: { text: string }): ReactElement {
  return <div className="text-[12px] text-[var(--ink-3)]">{text}</div>;
}

function SkeletonRow(): ReactElement {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4">
          <div className="h-3 w-1/2 rounded bg-[var(--bg-soft)] mb-3" />
          <div className="h-3 w-2/3 rounded bg-[var(--bg-soft)]" />
        </div>
      ))}
    </div>
  );
}
